import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from "@angular/core";
import { CustomWidgetFullscreenChartMode, CustomWidgetModel } from "app/models/customWidget.model";
import { AlarmModelTimeWindow } from "app/models/alarm.model";
import { DataTypeModelKey } from "app/models/dataType.model";
import { DeviceAggregatedData, DeviceDataModel, DeviceDataType } from "app/models/deviceData.model";
import { EventModelSeverity } from "app/models/event.model";
import { ProjectDeviceModel } from "app/models/projectDevice.model";
import { DataService } from "app/services/api/data.service";
import { DeviceMqttService } from "app/services/api/device.mqtt.service";
import { ProjectDeviceService } from "app/services/api/projectDevice.service";
import { ContextService } from "app/services/context.service";
import * as Highcharts from "highcharts/highcharts";
import { IMqttMessage } from "ngx-mqtt";
import { ToastrService } from "ngx-toastr";
import { BehaviorSubject, Subscription } from "rxjs";
import * as moment from "moment";
import { chartSoundPressure5MinutesCard } from "app/chart-config/chart-sound-pressure-5-minutes-card";

type WidgetMetric = {
    key: DataTypeModelKey;
    value: string;
    timestamp: Date | null;
    status: EventModelSeverity;
};

@Component({
    selector: "app-custom-widget-card",
    templateUrl: "./custom-widget-card.component.html",
    styleUrls: ["./custom-widget-card.component.scss"],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomWidgetCardComponent implements OnInit, OnChanges, OnDestroy {
    @Input() widget: CustomWidgetModel | null = null;

    readonly device$ = new BehaviorSubject<ProjectDeviceModel | null>(null);

    loadingData = false;
    deviceData: DeviceDataModel[] = [];
    chartRef: Highcharts.Chart | null = null;
    Highcharts = Highcharts;
    chart = JSON.parse(JSON.stringify(chartSoundPressure5MinutesCard));

    private subscriptions: Subscription[] = [];

    constructor(
        private readonly dataService: DataService,
        private readonly projectDeviceService: ProjectDeviceService,
        private readonly deviceMqtt: DeviceMqttService,
        public readonly contextService: ContextService,
        private readonly toastr: ToastrService,
        private readonly cdr: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.reloadWidget();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["widget"]) {
            this.reloadWidget();
        }
    }

    ngOnDestroy(): void {
        this.clearSubscriptions();
    }

    get title(): string {
        if (this.widget?.name) {
            return this.widget.name;
        }

        const primary = this.widget?.primaryMetrics?.length || 0;
        const secondary = this.widget?.secondaryMetrics?.length || 0;
        return `Custom widget (${primary}/${secondary})`;
    }

    get primaryMetrics(): WidgetMetric[] {
        return (this.widget?.primaryMetrics || []).map((metric) => this.metricValue(metric));
    }

    get secondaryMetrics(): WidgetMetric[] {
        return (this.widget?.secondaryMetrics || []).map((metric) => this.metricValue(metric));
    }

    get latestPrimaryTimestamp(): Date | null {
        const timestamps = this.primaryMetrics
            .map((metric) => (metric.timestamp ? new Date(metric.timestamp) : null))
            .filter((timestamp): timestamp is Date => !!timestamp && !Number.isNaN(timestamp.getTime()))
            .sort((left, right) => right.getTime() - left.getTime());

        return timestamps[0] || null;
    }

    get shouldShowChart(): boolean {
        if (!this.widget?.chartMetric) {
            return false;
        }

        if (this.contextService.isKioskModeEnabled && this.widget.fullscreenChartMode === CustomWidgetFullscreenChartMode.hide) {
            return false;
        }

        return !!this.device$.value?.lastData?.[this.widget.chartMetric];
    }

    get hasAnyData(): boolean {
        const device = this.device$.value;
        if (!device?.lastData || !this.widget) {
            return false;
        }

        const metrics = [
            ...(this.widget.primaryMetrics || []),
            ...(this.widget.secondaryMetrics || []),
            ...(this.widget.chartMetric ? [this.widget.chartMetric] : []),
        ];

        return metrics.some((metric) => !!device.lastData?.[metric]);
    }

    chartCallback: Highcharts.ChartCallbackFunction = (chart) => {
        this.chartRef = chart;
        this.applyChartData();
    };

    metricLabel(metric: DataTypeModelKey): string {
        return metric.replaceAll("_", " ");
    }

    private reloadWidget() {
        if (!this.widget?.projectDeviceId) {
            return;
        }

        this.clearSubscriptions();
        this.fetchDevice();
        this.fetchChartData();
    }

    private fetchDevice() {
        this.projectDeviceService.get(this.widget.projectDeviceId).subscribe(
            (device) => {
                this.device$.next(Object.assign(new ProjectDeviceModel(), device));
                this.subscribeToTopics();
                this.cdr.markForCheck();
            },
            (error) => {
                console.log(error);
                this.toastr.error("Error: " + error.message);
            },
        );
    }

    private fetchChartData() {
        if (!this.widget?.chartMetric) {
            return;
        }

        this.loadingData = true;
        this.dataService.getDeviceDataSample(this.widget.projectDeviceId, [this.widget.chartMetric], 12, true).subscribe(
            (data: DeviceAggregatedData) => {
                this.deviceData = [...(data[this.widget.chartMetric] || [])].sort(
                    (d1, d2) => new Date(d1.date).getTime() - new Date(d2.date).getTime(),
                );
                this.applyChartStyle();
                this.applyChartData();
                this.loadingData = false;
                this.cdr.markForCheck();
            },
            (error) => {
                console.log(error);
                this.toastr.error("Error: " + error.message);
                this.loadingData = false;
                this.cdr.markForCheck();
            },
        );
    }

    private subscribeToTopics() {
        if (!this.widget || !this.device$.value?.id) {
            return;
        }

        const deviceId = this.device$.value.id;
        const metrics = Array.from(
            new Set([
                ...(this.widget.primaryMetrics || []),
                ...(this.widget.secondaryMetrics || []),
                ...(this.widget.chartMetric ? [this.widget.chartMetric] : []),
            ]),
        );

        this.subscriptions.push(
            this.deviceMqtt.deviceTopic(deviceId, DeviceDataType.status, null).subscribe((data) => {
                const item = JSON.parse(data.payload.toString());
                const device = this.device$.value;
                if (!device) {
                    return;
                }
                device.connected = item.connected;
                device.activeEvents = item.activeEvents;
                this.device$.next(device);
                this.cdr.markForCheck();
            }),
        );

        metrics.forEach((metric) => {
            this.subscriptions.push(
                this.deviceMqtt.deviceTopic(deviceId, DeviceDataType.instant, metric).subscribe((data: IMqttMessage) => {
                    const item = JSON.parse(data.payload.toString());
                    const device = this.device$.value;
                    if (!device) {
                        return;
                    }
                    device.lastData = Object.assign({}, device.lastData || {}, {
                        [metric]: {
                            value: item.value,
                            timestamp: item.timestamp,
                        },
                    }) as unknown as ProjectDeviceModel["lastData"];
                    this.device$.next(device);
                    this.cdr.markForCheck();
                }),
            );
        });

        if (this.widget.chartMetric) {
            this.subscriptions.push(
                this.deviceMqtt.deviceTopic(deviceId, DeviceDataType.bucket, this.widget.chartMetric).subscribe((data: IMqttMessage) => {
                    const item = JSON.parse(data.payload.toString());
                    const last = this.deviceData[this.deviceData.length - 1];
                    if (last && moment(last.date).format("HH:mm") === moment(item.date).format("HH:mm")) {
                        this.deviceData[this.deviceData.length - 1].aggregation = item.aggregation;
                    } else {
                        this.deviceData = [...this.deviceData.slice(-11), item];
                    }
                    this.applyChartData();
                    this.cdr.markForCheck();
                }),
            );
        }
    }

    private clearSubscriptions() {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
        this.subscriptions = [];
    }

    private metricValue(metric: DataTypeModelKey): WidgetMetric {
        const device = this.device$.value;
        const data = device?.lastData?.[metric];
        return {
            key: metric,
            value: this.formatMetricValue(metric, data?.value),
            timestamp: data?.timestamp || null,
            status:
                device?.getAlarmStatus(metric, AlarmModelTimeWindow.AlarmModelTimeWindowInstant) ||
                EventModelSeverity.EventModelSeveritySuccess,
        };
    }

    private formatMetricValue(metric: DataTypeModelKey, value: unknown): string {
        if (value === undefined || value === null) {
            return "-";
        }

        if (
            metric === DataTypeModelKey.powerSupplyStatus ||
            metric === DataTypeModelKey.overload ||
            metric === DataTypeModelKey.underrange
        ) {
            return value ? "On" : "Off";
        }

        if (typeof value === "number") {
            return `${value.toFixed(this.metricDecimals(metric))} ${this.metricUnit(metric)}`.trim();
        }

        return `${value}`;
    }

    private metricDecimals(metric: DataTypeModelKey): number {
        if (
            metric === DataTypeModelKey.battery ||
            metric === DataTypeModelKey.cellullarSignalLevel ||
            metric === DataTypeModelKey.wifiSignalLevel ||
            metric === DataTypeModelKey.freeStorage
        ) {
            return 0;
        }

        return 1;
    }

    private metricUnit(metric: DataTypeModelKey): string {
        switch (metric) {
            case DataTypeModelKey.soundPressureLevel:
            case DataTypeModelKey.rolling5:
            case DataTypeModelKey.rolling5s:
                return "dBA";
            case DataTypeModelKey.soundPressureLevelC:
            case DataTypeModelKey.rolling5C:
            case DataTypeModelKey.rolling5sC:
                return "dBC";
            case DataTypeModelKey.soundPressureLevelCMinusA:
                return "dB";
            case DataTypeModelKey.battery:
            case DataTypeModelKey.cellullarSignalLevel:
            case DataTypeModelKey.wifiSignalLevel:
            case DataTypeModelKey.freeStorage:
                return "%";
            case DataTypeModelKey.temperature:
                return "ºC";
            default:
                return "";
        }
    }

    private applyChartStyle() {
        if (!this.widget?.chartMetric) {
            return;
        }

        const suffix = this.metricUnit(this.widget.chartMetric);
        const yAxis = this.chart.options.yAxis?.[0];
        this.chart.options.tooltip = {
            ...this.chart.options.tooltip,
            valueDecimals: this.metricDecimals(this.widget.chartMetric),
            valueSuffix: suffix ? ` ${suffix}` : "",
        };

        if (yAxis) {
            if (
                this.widget.chartMetric === DataTypeModelKey.battery ||
                this.widget.chartMetric === DataTypeModelKey.cellullarSignalLevel ||
                this.widget.chartMetric === DataTypeModelKey.wifiSignalLevel ||
                this.widget.chartMetric === DataTypeModelKey.freeStorage
            ) {
                yAxis.min = 0;
                yAxis.max = 100;
                yAxis.ceiling = 105;
            } else {
                yAxis.min = 0;
                yAxis.max = undefined;
                yAxis.ceiling = undefined;
                yAxis.softMax = 120;
            }
        }
    }

    private applyChartData() {
        if (!this.widget?.chartMetric) {
            return;
        }

        const seriesData = this.deviceData.map((data) => {
            const value = Math.round((data.aggregation?.average || 0) * 10) / 10;
            return [moment(data.date).format("HH:mm"), value];
        });

        this.chart.options.series[0].data = seriesData;
        this.chart.options.series[0].name = this.metricLabel(this.widget.chartMetric);

        if (this.chartRef) {
            this.chartRef.update(this.chart.options, true, true);
        }
    }
}
